import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AdHocRecordsQuery, AnalyticsFilter, InteractionRecord } from '@/shared/analytics/AnalyticsTypes';

/** Higher than the evidence pages' 250 — this grid is the full feed and pages client-side. */
const ACTIVITY_RECORD_LIMIT = 1000;

/** Which slice of the feed survives the record limit, e.g. newest-first or heaviest-first. */
export type ActivityOrder = NonNullable<AdHocRecordsQuery['orderBy']>;

const NEWEST_FIRST: ActivityOrder = { column: 'timestamp', direction: 'desc' };

/**
 * Backs a surface's Activity tab: the newest-first feed of raw interaction
 * turns across the whole workspace, sliced only by the page's ambient filters.
 */
@injectable()
export class SurfaceActivityViewModel extends BaseViewModel {
    records: InteractionRecord[] = [];
    totalMatched = 0;
    loading = false;
    errorMessage: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway) {
        super();
    }

    async load(filters: AnalyticsFilter[], orderBy: ActivityOrder = NEWEST_FIRST): Promise<void> {
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const result = await this.gateway.records({
                source: 'interactions',
                filters,
                orderBy,
                limit: ACTIVITY_RECORD_LIMIT,
            });
            this.records = result.records;
            this.totalMatched = result.totalMatched;
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
